import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { from, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { SearchItem } from '../models/search-item.model';

@Injectable({
  providedIn: 'root'
})
export class SearchIndexService {
  private indexCache: SearchItem[] = [];
  private isLoaded = false;
  private loadPromise: Promise<SearchItem[]> | null = null;

  // Synonyms map as requested
  private readonly SYNONYMS: Record<string, string[]> = {
    'phone': ['contact', 'number', 'call', 'support', 'telephone', 'phone'],
    'contact': ['phone', 'email', 'address', 'mail', 'support', 'contact'],
    'email': ['contact', 'mail', 'message', 'email'],
    'career': ['jobs', 'careers', 'hiring', 'openings', 'positions', 'apply', 'career'],
    'jobs': ['career', 'careers', 'hiring', 'openings', 'positions', 'apply', 'jobs'],
    'ai': ['artificial intelligence', 'automation', 'genai', 'copilot', 'mlops', 'ml', 'ai'],
    'artificial intelligence': ['ai', 'automation', 'genai', 'copilot', 'mlops', 'ml'],
    'cloud': ['cloud migration', 'aws', 'azure', 'gcp', 'native', 'serverless', 'microservices', 'cloud'],
    'cloud migration': ['cloud', 'modernization', 'migration']
  };

  // Ordering based on page type priority
  private readonly PAGE_TYPE_PRIORITY: Record<string, number> = {
    'service': 50,
    'insight': 40,
    'career': 30,
    'contact': 20,
    'about': 10,
    'page': 0
  };

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  /**
   * Lazy load the search index JSON file.
   */
  loadIndex(): Observable<SearchItem[]> {
    if (this.isLoaded) {
      return of(this.indexCache);
    }

    if (!isPlatformBrowser(this.platformId)) {
      return of([]);
    }

    if (this.loadPromise) {
      return from(this.loadPromise);
    }

    this.loadPromise = fetch('/assets/search-index.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load search-index.json');
        }
        return response.json();
      })
      .then((data: SearchItem[]) => {
        this.indexCache = data;
        this.isLoaded = true;
        return data;
      })
      .catch(error => {
        console.error('Error loading search index:', error);
        this.loadPromise = null;
        return [];
      });

    return from(this.loadPromise);
  }

  /**
   * Search query engine with priority sorting and synonyms.
   */
  search(query: string): SearchItem[] {
    const term = query.trim().toLowerCase();
    if (term.length < 2 || this.indexCache.length === 0) {
      return [];
    }

    // Split search query into individual terms
    const queryTerms = term.split(/\s+/).filter(t => t.length >= 2);
    if (queryTerms.length === 0) {
      return [];
    }

    const matchedItems: { item: SearchItem; score: number }[] = [];

    for (const item of this.indexCache) {
      let score = 0;
      const itemTitle = item.title.toLowerCase();
      const itemDesc = (item.description || '').toLowerCase();
      const itemSection = (item.section || '').toLowerCase();
      const itemKeywords = (item.keywords || []).map(k => k.toLowerCase());
      const itemPriority = item.priority || 1;

      // Check full string match matches first
      if (itemTitle === term) {
        score += 2000 * itemPriority;
      } else if (itemTitle.startsWith(term)) {
        score += 1000 * itemPriority;
      } else if (itemTitle.includes(term)) {
        score += 600 * itemPriority;
      }

      // Check per-word match (including synonyms)
      for (const qWord of queryTerms) {
        // Build candidate search terms list: word itself (weight 1.0) and synonyms (weight 0.4)
        const candidates = [{ word: qWord, weight: 1.0 }];
        
        // Find synonyms
        if (this.SYNONYMS[qWord]) {
          this.SYNONYMS[qWord].forEach(syn => {
            candidates.push({ word: syn, weight: 0.4 });
          });
        }

        for (const cand of candidates) {
          const w = cand.word;
          const wt = cand.weight;

          // Title matching
          if (itemTitle === w) {
            score += 1000 * wt * itemPriority;
          } else if (itemTitle.startsWith(w)) {
            score += 500 * wt * itemPriority;
          } else if (itemTitle.includes(w)) {
            score += 300 * wt * itemPriority;
          }

          // Keyword matching
          if (itemKeywords.includes(w)) {
            score += 200 * wt * itemPriority;
          } else if (itemKeywords.some(k => k.includes(w))) {
            score += 100 * wt * itemPriority;
          }

          // Section matching
          if (itemSection.includes(w)) {
            score += 150 * wt;
          }

          // Description matching
          if (itemDesc.includes(w)) {
            score += 80 * wt;
          }
        }
      }

      if (score > 0) {
        // Enforce exact title matches highest by injecting a massive score boost
        // Service / Insight type priority mapping
        const typePriority = this.PAGE_TYPE_PRIORITY[item.pageType.toLowerCase()] || 0;
        
        // Final score combines text match score and type/priority biases
        const finalScore = score + typePriority;

        // Clone and highlight item
        const highlightedItem: SearchItem = {
          ...item,
          highlightedTitle: this.highlightKeyword(item.title, query),
          highlightedDescription: this.highlightKeyword(item.description || '', query)
        };

        matchedItems.push({ item: highlightedItem, score: finalScore });
      }
    }

    // Sort by score descending, then by pageType ordering bias
    return matchedItems
      .sort((a, b) => b.score - a.score)
      .map(m => m.item)
      .slice(0, 10);
  }

  /**
   * Highlight searched terms in text using safe HTML wrapper tags.
   */
  private highlightKeyword(text: string, term: string): string {
    if (!text) return '';

    // HTML escape to prevent markup issues or XSS
    let escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    if (!term || term.trim().length === 0) {
      return escaped;
    }

    const queryWords = term.toLowerCase().split(/\s+/).filter(w => w.length >= 2);
    if (queryWords.length === 0) return escaped;

    // Collect all query terms & synonyms to match for highlighting
    const highlightPatterns: string[] = [];
    for (const qWord of queryWords) {
      highlightPatterns.push(this.escapeRegExp(qWord));
      
      // Also highlight exact synonyms if they match in the text
      if (this.SYNONYMS[qWord]) {
        this.SYNONYMS[qWord].forEach(syn => {
          highlightPatterns.push(this.escapeRegExp(syn));
        });
      }
    }

    // De-duplicate patterns and sort by length descending to match longer strings first
    const uniquePatterns = Array.from(new Set(highlightPatterns)).sort((a, b) => b.length - a.length);
    if (uniquePatterns.length === 0) return escaped;

    const regex = new RegExp(`(${uniquePatterns.join('|')})`, 'gi');
    return escaped.replace(regex, '<mark class="search-highlight" style="background: rgba(95, 181, 200, 0.25); color: #fff; padding: 2px 4px; border-radius: 4px; font-weight: bold;">$1</mark>');
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
